import { useState } from 'react'
import { Plus, ShieldCheck, Star, Trash2, Pencil } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { toApiProblem } from '@/lib/api/client'
import { useGeofenceLocations, useGeofenceMutations, useGeofencePolicies } from '../../api/geofence'
import {
  FACE_MATCH_MODES, FACE_STRICTNESS, GEOFENCE_ENFORCEMENTS, GEOFENCE_MODES, PHOTO_MODES,
} from '../../types/geofence'
import type {
  FaceMatchMode, FaceStrictness, GeofenceEnforcement, GeofenceMode, GeofencePolicyView, PhotoMode,
  SaveGeofencePolicy, UnenrolledAction,
} from '../../types/geofence'
import { formatMetres } from './geo'
import { Toggle } from './shared'

const BLANK: SaveGeofencePolicy = {
  code: '', name: '', description: null, mode: 'OWN_LOCATION', enforcement: 'BLOCK',
  applyOnPunchIn: true, applyOnPunchOut: true, requireLocation: true, maxAccuracyMetres: 100,
  accuracyTolerance: true, bufferMetres: 0, blockMockLocation: true,
  allowOutsideWithReason: false, defaultPolicy: false, active: true, locationIds: [],
  photoMode: 'OFF', photoOnPunchIn: true, photoOnPunchOut: true, faceMatch: 'OFF',
  faceStrictness: 'MEDIUM', faceMaxAttempts: 3, livenessRequired: false,
  unenrolledAction: 'ENROL_REQUIRED',
}

function fromView(view: GeofencePolicyView): SaveGeofencePolicy & { id: string } {
  return {
    id: view.id, code: view.code, name: view.name, description: view.description,
    mode: view.mode, enforcement: view.enforcement, applyOnPunchIn: view.applyOnPunchIn,
    applyOnPunchOut: view.applyOnPunchOut, requireLocation: view.requireLocation,
    maxAccuracyMetres: view.maxAccuracyMetres, accuracyTolerance: view.accuracyTolerance,
    bufferMetres: view.bufferMetres,
    blockMockLocation: view.blockMockLocation, allowOutsideWithReason: view.allowOutsideWithReason,
    defaultPolicy: view.defaultPolicy, active: view.active,
    locationIds: view.locations.map((site) => site.id),
    photoMode: view.photoMode, photoOnPunchIn: view.photoOnPunchIn,
    photoOnPunchOut: view.photoOnPunchOut, faceMatch: view.faceMatch,
    faceStrictness: view.faceStrictness, faceMaxAttempts: view.faceMaxAttempts,
    livenessRequired: view.livenessRequired, unenrolledAction: view.unenrolledAction,
  }
}

function PolicyDialog({
  initial, onClose,
}: {
  initial: SaveGeofencePolicy & { id?: string }
  onClose: () => void
}) {
  const { savePolicy } = useGeofenceMutations()
  const sites = useGeofenceLocations()
  const [draft, setDraft] = useState(initial)
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof SaveGeofencePolicy>(key: K, value: SaveGeofencePolicy[K]) =>
    setDraft((previous) => ({ ...previous, [key]: value }))

  const restricted = draft.mode !== 'UNRESTRICTED'
  const listsSites = draft.mode === 'SPECIFIC_LOCATIONS' || draft.mode === 'OWN_LOCATION'

  function toggleSite(id: string, on: boolean) {
    set('locationIds', on ? [...draft.locationIds, id] : draft.locationIds.filter((x) => x !== id))
  }

  function submit() {
    setError(null)
    savePolicy.mutate(draft, {
      onSuccess: onClose,
      onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'The policy could not be saved.'),
    })
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => { if (!open) onClose() }}
      title={initial.id ? `Edit ${initial.name}` : 'New geofence policy'}
      description="Which sites count, and how strictly a punch from elsewhere is treated."
      size="lg"
      submitLabel="Save policy"
      loading={savePolicy.isPending}
      error={error}
      onSubmit={submit}
    >
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="gp-name" required>Name</Label>
            <Input id="gp-name" required value={draft.name} placeholder="Office staff"
              onChange={(event) => set('name', event.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="gp-code" required>Code</Label>
            <Input id="gp-code" required value={draft.code} placeholder="OFFICE"
              onChange={(event) => set('code', event.target.value.toUpperCase())} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="gp-desc">Description</Label>
            <Textarea id="gp-desc" rows={2} value={draft.description ?? ''}
              onChange={(event) => set('description', event.target.value || null)} />
          </div>
        </div>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-semibold text-[var(--color-heading)]">Where they may punch</legend>
          {(Object.keys(GEOFENCE_MODES) as GeofenceMode[]).map((mode) => (
            <label key={mode} className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3 has-[:checked]:border-[var(--color-primary)] has-[:checked]:bg-[var(--color-primary-soft)]">
              <input type="radio" name="gp-mode" className="mt-1 accent-[var(--color-primary)]"
                checked={draft.mode === mode} onChange={() => set('mode', mode)} />
              <span>
                <span className="block text-sm font-medium text-[var(--color-heading)]">{GEOFENCE_MODES[mode].label}</span>
                <span className="block text-xs text-[var(--color-muted)]">{GEOFENCE_MODES[mode].hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {listsSites && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-[var(--color-heading)]">
              {draft.mode === 'SPECIFIC_LOCATIONS' ? 'Permitted sites' : 'Extra sites (besides their own)'}
            </p>
            {sites.isPending && <Skeleton className="h-16" />}
            <div className="grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
              {sites.data?.filter((site) => site.active).map((site) => (
                <Toggle key={site.id} id={`gp-site-${site.id}`} label={site.name}
                  hint={site.fenced ? `${formatMetres(site.radiusMetres)} fence` : 'No geofence yet — set it on the Sites tab'}
                  checked={draft.locationIds.includes(site.id)}
                  onChange={(on) => toggleSite(site.id, on)} />
              ))}
            </div>
          </div>
        )}

        {restricted && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="gp-enforcement">Outside the fence</Label>
                <Select id="gp-enforcement" value={draft.enforcement}
                  onChange={(event) => set('enforcement', event.target.value as GeofenceEnforcement)}>
                  {(Object.keys(GEOFENCE_ENFORCEMENTS) as GeofenceEnforcement[]).map((value) => (
                    <option key={value} value={value}>
                      {GEOFENCE_ENFORCEMENTS[value].label} — {GEOFENCE_ENFORCEMENTS[value].hint}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="gp-buffer">Extra margin on every fence (m)</Label>
                <Input id="gp-buffer" type="number" min={0} max={5000} value={draft.bufferMetres}
                  onChange={(event) => set('bufferMetres', Number(event.target.value || 0))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="gp-accuracy">Refuse readings less accurate than (m)</Label>
                <Input id="gp-accuracy" type="number" min={5} max={5000} placeholder="No limit"
                  value={draft.maxAccuracyMetres ?? ''}
                  onChange={(event) => set('maxAccuracyMetres', event.target.value === '' ? null : Number(event.target.value))} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle id="gp-in" label="Check punch-in" checked={draft.applyOnPunchIn}
                onChange={(on) => set('applyOnPunchIn', on)} />
              <Toggle id="gp-out" label="Check punch-out" hint="Turn off for people who leave from the field."
                checked={draft.applyOnPunchOut} onChange={(on) => set('applyOnPunchOut', on)} />
              <Toggle id="gp-gps" label="Location must be on" hint="Refuse a punch that arrives with no GPS position."
                checked={draft.requireLocation} onChange={(on) => set('requireLocation', on)} />
              <Toggle id="gp-tolerance" label="Allow for GPS accuracy at the edge"
                hint="A reading that overlaps the fence counts as inside."
                checked={draft.accuracyTolerance} onChange={(on) => set('accuracyTolerance', on)} />
              <Toggle id="gp-mock" label="Block fake (mock) locations"
                checked={draft.blockMockLocation} onChange={(on) => set('blockMockLocation', on)} />
              <Toggle id="gp-reason" label="Allow outside with a reason"
                hint="They can still punch by saying why; the punch is flagged."
                checked={draft.allowOutsideWithReason} onChange={(on) => set('allowOutsideWithReason', on)} />
            </div>
          </>
        )}

        <fieldset className="space-y-4 border-t border-[var(--color-border)] pt-4">
          <legend className="text-sm font-semibold text-[var(--color-heading)]">Photo &amp; face</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="gp-photo">Photo with each punch</Label>
              <Select id="gp-photo" value={draft.faceMatch !== 'OFF' ? 'REQUIRED' : draft.photoMode}
                disabled={draft.faceMatch !== 'OFF'}
                onChange={(event) => set('photoMode', event.target.value as PhotoMode)}>
                {(Object.keys(PHOTO_MODES) as PhotoMode[]).map((value) => (
                  <option key={value} value={value}>{PHOTO_MODES[value]}</option>
                ))}
              </Select>
              {draft.faceMatch !== 'OFF' && (
                <p className="text-xs text-[var(--color-muted)]">Required while the face is checked.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="gp-face">Match the face</Label>
              <Select id="gp-face" value={draft.faceMatch}
                onChange={(event) => set('faceMatch', event.target.value as FaceMatchMode)}>
                {(Object.keys(FACE_MATCH_MODES) as FaceMatchMode[]).map((value) => (
                  <option key={value} value={value}>{FACE_MATCH_MODES[value].label}</option>
                ))}
              </Select>
              <p className="text-xs text-[var(--color-muted)]">{FACE_MATCH_MODES[draft.faceMatch].hint}</p>
            </div>
          </div>

          {(draft.photoMode !== 'OFF' || draft.faceMatch !== 'OFF') && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle id="gp-photo-in" label="On punch-in" checked={draft.photoOnPunchIn}
                onChange={(on) => set('photoOnPunchIn', on)} />
              <Toggle id="gp-photo-out" label="On punch-out" checked={draft.photoOnPunchOut}
                onChange={(on) => set('photoOnPunchOut', on)} />
            </div>
          )}

          {draft.faceMatch !== 'OFF' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="gp-strict">Strictness</Label>
                <Select id="gp-strict" value={draft.faceStrictness}
                  onChange={(event) => set('faceStrictness', event.target.value as FaceStrictness)}>
                  {(Object.keys(FACE_STRICTNESS) as FaceStrictness[]).map((value) => (
                    <option key={value} value={value}>{FACE_STRICTNESS[value].label}</option>
                  ))}
                </Select>
                <p className="text-xs text-[var(--color-muted)]">{FACE_STRICTNESS[draft.faceStrictness].hint}</p>
              </div>
              {draft.faceMatch === 'BLOCK' && (
                <div className="space-y-1">
                  <Label htmlFor="gp-attempts">Tries before it is accepted and flagged</Label>
                  <Input id="gp-attempts" type="number" min={1} max={10} value={draft.faceMaxAttempts}
                    onChange={(event) => set('faceMaxAttempts', Number(event.target.value || 3))} />
                  <p className="text-xs text-[var(--color-muted)]">Counted over 15 minutes. Stops bad light costing someone their attendance.</p>
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="gp-unenrolled">If no face is registered yet</Label>
                <Select id="gp-unenrolled" value={draft.unenrolledAction}
                  onChange={(event) => set('unenrolledAction', event.target.value as UnenrolledAction)}>
                  <option value="ENROL_REQUIRED">Refuse until they register</option>
                  <option value="ALLOW_FLAG">Accept and flag</option>
                </Select>
              </div>
              <Toggle id="gp-liveness" label="Live-face check required"
                hint="Blink or head turn in the mobile app, against printed photos. The web punch cannot do this, so web punches are refused under this setting."
                checked={draft.livenessRequired} onChange={(on) => set('livenessRequired', on)} />
            </div>
          )}
        </fieldset>

        <div className="grid gap-3 border-t border-[var(--color-border)] pt-4 sm:grid-cols-2">
          <Toggle id="gp-default" label="Company default"
            hint="Applies to everyone not covered by an assignment. Only one policy can be the default."
            checked={draft.defaultPolicy} onChange={(on) => set('defaultPolicy', on)} />
          <Toggle id="gp-active" label="Active" hint="An inactive policy is skipped as if it were not assigned."
            checked={draft.active} onChange={(on) => set('active', on)} />
        </div>
      </div>
    </FormDialog>
  )
}

function summary(policy: GeofencePolicyView): string {
  if (policy.mode === 'UNRESTRICTED') return 'Location: anywhere'
  const checks = [
    policy.applyOnPunchIn && policy.applyOnPunchOut ? 'in and out'
      : policy.applyOnPunchIn ? 'punch-in only' : 'punch-out only',
    policy.maxAccuracyMetres ? `accuracy ≤ ${policy.maxAccuracyMetres} m` : null,
    policy.bufferMetres ? `+${policy.bufferMetres} m margin` : null,
    policy.allowOutsideWithReason ? 'reason allowed outside' : null,
  ].filter(Boolean)
  return `Checks ${checks.join(' · ')}`
}

function faceSummary(policy: GeofencePolicyView): string {
  if (policy.faceMatch === 'OFF') {
    return policy.photoMode === 'OFF' ? 'No photo' : PHOTO_MODES[policy.photoMode]
  }
  const when = policy.photoOnPunchIn && policy.photoOnPunchOut ? 'in and out'
    : policy.photoOnPunchIn ? 'punch-in' : 'punch-out'
  return [
    `${FACE_MATCH_MODES[policy.faceMatch].label} on ${when}`,
    `${FACE_STRICTNESS[policy.faceStrictness].label.replace(' (recommended)', '')} strictness`,
    policy.faceMatch === 'BLOCK' ? `${policy.faceMaxAttempts} tries` : null,
    policy.livenessRequired ? 'live-face check' : null,
    policy.unenrolledAction === 'ENROL_REQUIRED' ? 'must register first' : 'unregistered: flagged',
  ].filter(Boolean).join(' · ')
}

/** The policies, as cards — a handful at most, and each needs reading rather than scanning. */
export function PoliciesTab() {
  const policies = useGeofencePolicies()
  const { deletePolicy } = useGeofenceMutations()
  const [editing, setEditing] = useState<(SaveGeofencePolicy & { id?: string }) | null>(null)
  const [removing, setRemoving] = useState<GeofencePolicyView | null>(null)

  if (policies.isPending) return <Skeleton className="h-48" />
  if (policies.isError) return <ErrorState error={policies.error} onRetry={() => void policies.refetch()} />

  return (
    <div className="space-y-4">
      {editing && <PolicyDialog initial={editing} onClose={() => setEditing(null)} />}
      {removing && (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open) setRemoving(null) }}
          title={`Delete ${removing.name}?`}
          description={`Its ${removing.assignmentCount} assignment(s) are removed too, and those people fall back to the next policy that reaches them — or to no geofence at all.`}
          confirmLabel="Delete policy"
          destructive
          loading={deletePolicy.isPending}
          onConfirm={() => deletePolicy.mutate(removing.id, { onSuccess: () => setRemoving(null) })}
        />
      )}

      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-[var(--color-muted)]">
          A policy decides which sites count and how strict to be. Put it on people in the Assignments tab.
        </p>
        <PermissionGate permission="attendance:write">
          <Button type="button" onClick={() => setEditing({ ...BLANK })}>
            <Plus className="size-4" aria-hidden="true" />
            New policy
          </Button>
        </PermissionGate>
      </div>

      {policies.data.length === 0 && (
        <EmptyState icon={ShieldCheck} title="No geofence policies yet"
          description="Until there is one, app punches are checked only against the site the phone names, and nobody is restricted to their own site. A common start: an 'Office staff' policy (own location, block) as the company default, and a 'Field staff' policy (anywhere) for drivers and sales." />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {policies.data.map((policy) => (
          <Card key={policy.id} className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-semibold text-[var(--color-heading)]">
                  {policy.name}
                  {policy.defaultPolicy && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary-soft)] px-2 py-0.5 text-xs text-[var(--color-primary)]">
                      <Star className="size-3" aria-hidden="true" /> Default
                    </span>
                  )}
                </p>
                <p className="text-xs text-[var(--color-muted)]">{policy.code}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {!policy.active && <StatusBadge status="INACTIVE" tone="neutral" label="Inactive" />}
                <PermissionGate permission="attendance:write">
                  <Button type="button" size="sm" variant="ghost" aria-label={`Edit ${policy.name}`}
                    onClick={() => setEditing(fromView(policy))}>
                    <Pencil className="size-4" aria-hidden="true" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" aria-label={`Delete ${policy.name}`}
                    onClick={() => setRemoving(policy)}>
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </PermissionGate>
              </div>
            </div>
            {policy.description && <p className="mt-2 text-sm text-[var(--color-body)]">{policy.description}</p>}
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Where</dt>
                <dd className="text-[var(--color-heading)]">{GEOFENCE_MODES[policy.mode].label}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--color-muted)]">Outside the fence</dt>
                <dd>
                  {policy.mode === 'UNRESTRICTED' ? '—' : (
                    <StatusBadge status={policy.enforcement}
                      tone={policy.enforcement === 'BLOCK' ? 'danger' : 'warning'}
                      label={GEOFENCE_ENFORCEMENTS[policy.enforcement].label} />
                  )}
                </dd>
              </div>
              {policy.locations.length > 0 && (
                <div className="col-span-2">
                  <dt className="text-xs text-[var(--color-muted)]">
                    {policy.mode === 'OWN_LOCATION' ? 'Extra sites' : 'Sites'}
                  </dt>
                  <dd className="text-[var(--color-heading)]">{policy.locations.map((site) => site.name).join(', ')}</dd>
                </div>
              )}
              <div className="col-span-2">
                <dt className="text-xs text-[var(--color-muted)]">Rules</dt>
                <dd className="text-[var(--color-body)]">{summary(policy)}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-xs text-[var(--color-muted)]">Photo &amp; face</dt>
                <dd className="text-[var(--color-body)]">{faceSummary(policy)}</dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-[var(--color-muted)]">
              {policy.assignmentCount} assignment{policy.assignmentCount === 1 ? '' : 's'}
              {policy.defaultPolicy ? ' · plus everyone not otherwise covered' : ''}
            </p>
          </Card>
        ))}
      </div>
    </div>
  )
}
